let youtubeVideosQueue = [];
let activePlayers = [];
let playerVars = {};

// Tracks iframes that have already been wrapped with YT.Player so we don't
// wrap them more than once when re-initializing.
const ytPlayersMap = new WeakMap();

const defaultPlayerVars = {
    'modestbranding': 1,
    'rel': 0,
    'playsinline': 1,
    'mute': 1
};

const VideoPlayers = {
    youtubeThumbnailNames: [
        'maxresdefault',
        'sddefault',
        'hqdefault',
        'mqdefault',
        'default',
    ],

    setYouTubeThumbnailSource(img, videoId, index = 0) {
        const thumbnailName = this.youtubeThumbnailNames[index];

        if (!img || !videoId || !thumbnailName) {
            return;
        }

        img.onerror = () => {
            this.setYouTubeThumbnailSource(img, videoId, index + 1);
        };

        img.src = `https://i.ytimg.com/vi/${videoId}/${thumbnailName}.jpg`;
    },

    init() {
        this.enhanceYouTubeEmbeds();
        this.initializeVideoPlayers();
        this.setupPauseOnPlayListeners();
        this.loadYouTubeAPI();
    },

    initializeVideoPlayers() {
        const self = this;
        const videoContainers = document.querySelectorAll('.js-video');

        if (videoContainers.length === 0) return;

        videoContainers.forEach(container => {
            const videoThumbnail = container.querySelector('.js-video-thumbnail');
            const videoPlayer = container.querySelector('.js-video-player');

            playerVars = defaultPlayerVars;

            // if videoContainer has .--has-autoplay class, play video automatically
            // when on viewport
            if (container.classList.contains('--has-autoplay') && videoThumbnail && videoPlayer) {
                setTimeout(() => { // Wait for oEmbed dependencies to load
                    self.autoplayVideo(container, videoThumbnail, videoPlayer);
                }, 1000);
            }

            if (!videoThumbnail || !videoPlayer) return;

            videoThumbnail.addEventListener('click', () => self.playVideo(videoThumbnail, videoPlayer, false));
            videoThumbnail.addEventListener('keypress', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    self.playVideo(videoThumbnail, videoPlayer, false);
                }
            });
        });
    },

    autoplayVideo(container, videoThumbnail, videoPlayer) {
        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    playerVars = {
                        'autoplay': 1,
                        'controls': 1,
                        'modestbranding': 1,
                        'rel': 0,
                        'showinfo': 0,
                        'loop': 1,
                        'mute': 1,
                        'playsinline': 1
                    };

                    this.playVideo(videoThumbnail, videoPlayer, true);
                    observer.disconnect();
                }
            });
        }, {
            threshold: 0.1 // 10% of the video is visible
        });

        observer.observe(container);
    },

    playVideo(thumbnail, player, autoplay) {
        thumbnail.classList.add('is-loading');

        this.pauseAllVideos();
        if (player.src.includes('youtube.com')) {
            this.playYouTubeVideo(thumbnail, player, autoplay);
        } else {
            this.playHTMLVideo(thumbnail, player);
        }
    },

    /**
     * Pause every .js-video-player within `scope`, optionally skipping one player.
     *
     * @param {Element|Document} [scope=document] - Limit the pause to players inside this element.
     * @param {Element|null}     [exceptPlayer=null] - A player to leave alone (e.g. the one that just started).
     */
    pauseAllVideos(scope = document, exceptPlayer = null) {
        const root = scope || document;
        const videoPlayers = root.querySelectorAll('.js-video-player');

        videoPlayers.forEach(player => {
            if (player === exceptPlayer) return;

            if (player.tagName.toLowerCase() === 'iframe') {
                // YouTube video
                if (player.src.includes('youtube.com')) {
                    try {
                        player.contentWindow.postMessage(JSON.stringify({
                            event: 'command',
                            func: 'pauseVideo'
                        }), '*');
                    } catch (e) { /* iframe may not be ready yet */ }
                }
            } else if (player instanceof HTMLVideoElement) {
                // HTML5 video
                if (typeof player.pause === 'function') {
                    player.pause();
                }
            }
        });
    },

    /**
     * Resolve the nearest tabs container for a given video player element, so we
     * only pause its siblings (other videos within the same tabs group).
     * Falls back to `document` when the player isn't inside a tabs container.
     *
     * @param {Element} player
     * @returns {Element|Document}
     */
    getPauseScope(player) {
        return player.closest('.tabs') || document;
    },

    /**
     * Attach play-event listeners so that when any video starts playing,
     * its sibling videos within the same tabs container are paused.
     *
     * - HTML5 <video>: listens to the native `play` event.
     * - YouTube iframes: wrapped with YT.Player and listens to onStateChange
     *   (see onYouTubeIframeAPIReady / wrapYouTubeIframe).
     */
    setupPauseOnPlayListeners() {
        const html5Players = document.querySelectorAll('video.js-video-player');
        html5Players.forEach(player => {
            if (player.dataset.pauseSiblingsBound === 'true') return;
            player.dataset.pauseSiblingsBound = 'true';
            player.addEventListener('play', () => {
                this.pauseAllVideos(this.getPauseScope(player), player);
            });
        });
    },

    /**
     * Wrap a single YouTube iframe with YT.Player so we can listen for
     * state changes (and pause siblings when it enters the PLAYING state).
     *
     * Safe to call repeatedly — iframes already wrapped are skipped.
     *
     * @param {HTMLIFrameElement} iframe
     */
    wrapYouTubeIframe(iframe) {
        if (!window.YT || !window.YT.Player) return;
        if (ytPlayersMap.has(iframe)) return;
        if (!iframe.src || !iframe.src.includes('youtube.com')) return;

        try {
            const ytPlayer = new YT.Player(iframe, {
                events: {
                    onStateChange: (event) => {
                        // YT.PlayerState.PLAYING === 1
                        if (event.data === 1) {
                            this.pauseAllVideos(this.getPauseScope(iframe), iframe);
                        }
                    },
                },
            });
            ytPlayersMap.set(iframe, ytPlayer);
        } catch (e) {
            // If the iframe is detached or YT refuses to wrap it, ignore.
        }
    },

    /**
     * Wrap every YouTube .js-video-player iframe currently on the page.
     */
    wrapAllYouTubeIframes() {
        const iframes = document.querySelectorAll('iframe.js-video-player');
        iframes.forEach(iframe => this.wrapYouTubeIframe(iframe));
    },

    playYouTubeVideo(thumbnail, player, autoplay = false) {
        if (!player) {
            console.error('Invalid player element');
            return;
        }

        // Get the current iframe src
        const currentSrc = player.src;
        
        // Parse the URL and add autoplay parameter
        const url = new URL(currentSrc);
        url.searchParams.set('autoplay', '1');
        if (!autoplay) {
            url.searchParams.set('mute', '0');
        }
        
        // Create new iframe with autoplay
        const newIframe = document.createElement('iframe');
        newIframe.src = url.toString();
        newIframe.className = player.className;
        newIframe.setAttribute('title', player.getAttribute('title') || '');
        newIframe.setAttribute('width', player.getAttribute('width') || '100%');
        newIframe.setAttribute('height', player.getAttribute('height') || 'auto');
        newIframe.setAttribute('allow', player.getAttribute('allow') || 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
        newIframe.setAttribute('allowfullscreen', player.getAttribute('allowfullscreen') || '');
        newIframe.style.opacity = '1';
        
        // Replace old iframe with new one
        const parent = player.parentNode;
        parent.replaceChild(newIframe, player);

        // Hide thumbnail and show video
        if (thumbnail) {
            thumbnail.style.display = 'none';
        }

        newIframe.classList.add('!opacity-100');

        // The previous iframe's YT.Player wrapper is now stale; wrap the new
        // iframe so we keep receiving onStateChange events for sibling pausing.
        if (window.YT && window.YT.Player) {
            this.wrapYouTubeIframe(newIframe);
        }
    },

    loadYouTubeAPI() {
        if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
            const tag = document.createElement('script');
            tag.src = "https://www.youtube.com/iframe_api";
            document.body.appendChild(tag);
        }

        // Chain our YT IFrame API-ready callback so we can wrap existing
        // iframes once YT is available, without clobbering any other handler.
        const previousCallback = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
            if (typeof previousCallback === 'function') {
                try { previousCallback(); } catch (e) { /* noop */ }
            }
            this.wrapAllYouTubeIframes();
        };

        // If the API was already ready before we registered, wrap now.
        if (window.YT && window.YT.Player) {
            this.wrapAllYouTubeIframes();
        }
    },

    playHTMLVideo(thumbnail, player) {
        if (typeof player.play === 'function') {
            activePlayers.push(player);
            player.play().catch(error => {
                console.warn('Error playing video:', error);
            });

            player.addEventListener('playing', () => {
                if (thumbnail) {
                    thumbnail.style.display = 'none';
                }
            });
        } else {
            console.warn('Video player does not support HTML5 video API.');
        }
    },

    enhanceYouTubeEmbeds() {
        // Find all elements with js-richtext-video class
        const videoContainers = document.querySelectorAll('.richtext figure iframe');

        videoContainers.forEach(container => {
            const figure = container.closest('figure');
            if (figure) {
                const iframe = figure.querySelector('iframe');
                if (iframe && iframe.src && iframe.src.includes('youtube.com/embed/')) {
                    // Add js-video-player class to the iframe
                    iframe.classList.add('js-video-player');

                    // Extract video ID
                    const videoId = iframe.src.split('/').pop().split('?')[0];

                    // Create thumbnail element
                    const thumbnailElement = document.createElement('div');
                    thumbnailElement.className = 'video-thumbnail js-video-thumbnail';
                    thumbnailElement.tabIndex = 0;
                    thumbnailElement.setAttribute('role', 'button');
                    thumbnailElement.setAttribute('aria-label', 'Play video');
                    thumbnailElement.innerHTML = `
                        <img alt="Video thumbnail">
                        <div class="video-play">
                            <svg xmlns="http://www.w3.org/2000/svg" width="100%" viewBox="0 0 70 71" fill="none">
                                <circle cx="35" cy="35.4839" r="35" fill="#F18A0F"></circle>
                                <path d="M48.6432 34.6359C49.2699 35.0276 49.2699 35.9402 48.6432 36.3319L27.53 49.5276C26.8639 49.9439 26 49.4651 26 48.6796L26 22.2881C26 21.5027 26.864 21.0239 27.53 21.4401L48.6432 34.6359Z" fill="white"></path>
                            </svg>
                        </div>
                    `;
                    this.setYouTubeThumbnailSource(thumbnailElement.querySelector('img'), videoId);

                    // Create a new div to wrap everything
                    const wrapperDiv = document.createElement('div');
                    wrapperDiv.className = 'video js-video [&>.video-wrapper>iframe]:opacity-0';

                    // Create video-wrapper div
                    const videoWrapper = document.createElement('div');
                    videoWrapper.className = 'video-wrapper';

                    // Move the iframe into the video-wrapper
                    videoWrapper.appendChild(iframe);

                    // Add the thumbnail to the video-wrapper
                    videoWrapper.appendChild(thumbnailElement);

                    // Add the video-wrapper to the main wrapper
                    wrapperDiv.appendChild(videoWrapper);

                    // Replace the original figure with the new structure
                    figure.parentNode.replaceChild(wrapperDiv, figure);
                }
            }
        });
    },

    parseCkEditorOembed() {
        let players = document.querySelectorAll('.richtext figure.media oembed');
        const self = this;

        players.forEach((player) => {
            let src = player.getAttribute('url');
            let figure = player.parentNode;

            let iframe = document.createElement('iframe');
            iframe.setAttribute('src', src);
            figure.replaceChild(iframe, player);


            // Now, re-initialize
            self.init()
        });
    }
};

export default VideoPlayers;
